import type { ExtractedDocumentData, DeadlineCategory } from '@/types/deadlines';

/**
 * Patrones de RegEx para extracción de datos SIN LLM
 */
const PATTERNS = {
  // Números de expediente
  expediente: [
    /(?:EXP(?:TE)?\.?|Expediente:?|N\.?º?:?|Nº?:?)\s*[:\s]*([A-Z0-9]{0,4}[\/-]?\d{1,6}[\/-]\d{2,6}(?:[\/-]\d{2,6})?)/gi,
    /(?:Ref(?:erencia)?\.?:?)\s*([A-Z0-9]{0,4}[\/-]?\d{1,6}[\/-]\d{2,6})/gi,
    /\b(\d{4}[\/-]\d{4,6})\b/g,
  ],
  
  // Fechas en varios formatos
  fechas: [
    // DD/MM/YYYY o DD-MM-YYYY
    /\b(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})\b/g,
    // DD de mes de YYYY
    /\b(\d{1,2})\s+de\s+(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)\s+de\s+(\d{4})\b/gi,
  ],
  
  // Plazos
  plazos: [
    /(?:plazo\s+de\s+)?(\d+)\s*(días?|meses?)\s*(hábiles?|naturales?)?/gi,
    /en\s+(?:el\s+)?plazo\s+(?:de\s+)?(\d+)\s*(días?|meses?)/gi,
    /(?:dentro\s+de(?:l\s+plazo\s+de)?\s+)?(\d+)\s*(días?|meses?)\s*(hábiles?|naturales?)?/gi,
  ],
  
  // Interesados
  interesado: [
    /(?:D\.?\s*[\/,]?\s*D(?:ña|ÑA)?\.?|Don\s*[\/,]?\s*Doña|Sr\.?\s*[\/,]?\s*Sra\.?)\s+([A-ZÁÉÍÓÚÑ][a-záéíóúñ]+(?:\s+[A-ZÁÉÍÓÚÑ][a-záéíóúñ]+){1,4})/gi,
    /(?:Solicitante|Interesado\/a?|presentada?\s+por|a\s+favor\s+de|a\s+nombre\s+de):?\s*([A-ZÁÉÍÓÚÑ][a-záéíóúñ]+(?:\s+[A-ZÁÉÍÓÚÑ][a-záéíóúñ]+){1,4})/gi,
    /(?:titular|propietario\/a?):?\s*([A-ZÁÉÍÓÚÑ][a-záéíóúñ]+(?:\s+[A-ZÁÉÍÓÚÑ][a-záéíóúñ]+){1,4})/gi,
  ],
};

// Palabras clave para categorizar
const CATEGORY_KEYWORDS: Record<DeadlineCategory, string[]> = {
  urbanismo: [
    'licencia', 'obra', 'urbanístico', 'urbanistica', 'edificación', 'edificacion',
    'PGOU', 'ocupación', 'ocupacion', 'construcción', 'construccion', 'actividad',
    'demolición', 'demolicion', 'parcelación', 'parcelacion', 'uso del suelo',
    'declaración responsable', 'comunicación previa', 'primera ocupación',
  ],
  contratacion: [
    'contrato', 'licitación', 'licitacion', 'adjudicación', 'adjudicacion',
    'pliego', 'oferta', 'propuesta', 'mesa de contratación', 'LCSP',
    'menor', 'negociado', 'abierto', 'restringido', 'garantía',
  ],
  hacienda: [
    'presupuesto', 'liquidación', 'liquidacion', 'factura', 'pago', 'tributo',
    'tasa', 'impuesto', 'IBI', 'ICIO', 'cuenta general', 'modelo',
    'declaración', 'ingreso', 'gasto', 'TRLRHL', 'recaudación',
  ],
  personal: [
    'funcionario', 'personal', 'nómina', 'nomina', 'OEP', 'RPT',
    'vacaciones', 'permiso', 'licencia', 'baja', 'EBEP', 'toma de posesión',
    'concurso', 'oposición', 'oposicion', 'bolsa de trabajo', 'selección',
  ],
  otros: [],
};

// Meses en español
const MONTHS: Record<string, number> = {
  enero: 1, febrero: 2, marzo: 3, abril: 4, mayo: 5, junio: 6,
  julio: 7, agosto: 8, septiembre: 9, octubre: 10, noviembre: 11, diciembre: 12,
};

/**
 * Extrae el número de expediente del texto
 */
function extractExpedienteNumber(text: string): { value: string | null; confidence: number } {
  for (const pattern of PATTERNS.expediente) {
    const matches = [...text.matchAll(pattern)];
    if (matches.length > 0) {
      // Tomar el primer match que tenga un formato válido
      for (const match of matches) {
        const expediente = match[1]?.trim();
        if (expediente && expediente.length >= 4) {
          return { value: expediente, confidence: 0.9 };
        }
      }
    }
  }
  return { value: null, confidence: 0 };
}

/**
 * Extrae fechas del texto
 */
function extractDates(text: string): { dates: Date[]; confidence: number } {
  const dates: Date[] = [];
  
  // Formato DD/MM/YYYY o DD-MM-YYYY
  const pattern1 = PATTERNS.fechas[0];
  const matches1 = [...text.matchAll(pattern1)];
  
  for (const match of matches1) {
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    let year = parseInt(match[3], 10);
    
    // Ajustar año de 2 dígitos
    if (year < 100) {
      year += year > 50 ? 1900 : 2000;
    }
    
    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
      dates.push(new Date(year, month - 1, day));
    }
  }
  
  // Formato "DD de mes de YYYY"
  const pattern2 = PATTERNS.fechas[1];
  const matches2 = [...text.matchAll(pattern2)];
  
  for (const match of matches2) {
    const day = parseInt(match[1], 10);
    const monthName = match[2].toLowerCase();
    const year = parseInt(match[3], 10);
    const month = MONTHS[monthName];
    
    if (month && day >= 1 && day <= 31) {
      dates.push(new Date(year, month - 1, day));
    }
  }
  
  // Ordenar por fecha más reciente primero
  dates.sort((a, b) => b.getTime() - a.getTime());
  
  return { dates, confidence: dates.length > 0 ? 0.8 : 0 };
}

/**
 * Extrae plazos del texto
 */
function extractPlazo(text: string): { days: number | null; type: 'habiles' | 'naturales' | null; confidence: number } {
  for (const pattern of PATTERNS.plazos) {
    const matches = [...text.matchAll(pattern)];
    if (matches.length > 0) {
      const match = matches[0];
      const value = parseInt(match[1], 10);
      const unit = match[2]?.toLowerCase() || 'días';
      const typeText = match[3]?.toLowerCase() || '';
      
      let days = value;
      if (unit.includes('mes')) {
        days = value * 30; // Aproximación
      }
      
      const type: 'habiles' | 'naturales' = typeText.includes('natural') ? 'naturales' : 'habiles';
      
      return { days, type, confidence: 0.85 };
    }
  }
  return { days: null, type: null, confidence: 0 };
}

/**
 * Extrae el nombre del interesado
 */
function extractInterestedParty(text: string): { value: string | null; confidence: number } {
  for (const pattern of PATTERNS.interesado) {
    const matches = [...text.matchAll(pattern)];
    if (matches.length > 0) {
      const name = matches[0][1]?.trim();
      if (name && name.length >= 5) {
        // Limpiar y capitalizar
        const cleanName = name
          .split(/\s+/)
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
        return { value: cleanName, confidence: 0.75 };
      }
    }
  }
  return { value: null, confidence: 0 };
}

/**
 * Detecta la categoría del documento
 */
function detectCategory(text: string): { category: DeadlineCategory; confidence: number } {
  const textLower = text.toLowerCase();
  const scores: Record<DeadlineCategory, number> = {
    urbanismo: 0,
    contratacion: 0,
    hacienda: 0,
    personal: 0,
    otros: 0,
  };
  
  for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
    for (const keyword of keywords) {
      const regex = new RegExp(keyword.toLowerCase(), 'gi');
      const matches = textLower.match(regex);
      if (matches) {
        scores[category as DeadlineCategory] += matches.length;
      }
    }
  }
  
  // Encontrar la categoría con mayor puntuación
  let maxScore = 0;
  let bestCategory: DeadlineCategory = 'otros';
  
  for (const [category, score] of Object.entries(scores)) {
    if (score > maxScore) {
      maxScore = score;
      bestCategory = category as DeadlineCategory;
    }
  }
  
  const confidence = maxScore > 3 ? 0.9 : maxScore > 1 ? 0.7 : maxScore > 0 ? 0.5 : 0;
  
  return { category: bestCategory, confidence };
}

/**
 * Genera un título basado en el contenido
 */
function generateTitle(text: string, category: DeadlineCategory): string | null {
  const lines = text.split('\n').filter(line => line.trim().length > 10);
  
  // Buscar líneas que parezcan títulos (mayúsculas, cortas, sin puntos)
  for (const line of lines.slice(0, 10)) {
    const trimmed = line.trim();
    if (
      trimmed.length >= 10 &&
      trimmed.length <= 100 &&
      !trimmed.endsWith('.') &&
      trimmed === trimmed.toUpperCase()
    ) {
      return trimmed
        .split(' ')
        .map(word => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ');
    }
  }
  
  // Buscar líneas que contengan palabras clave de la categoría
  const keywords = CATEGORY_KEYWORDS[category];
  for (const line of lines.slice(0, 15)) {
    const trimmed = line.trim();
    for (const keyword of keywords) {
      if (trimmed.toLowerCase().includes(keyword.toLowerCase()) && trimmed.length <= 80) {
        return trimmed.length > 60 ? trimmed.substring(0, 60) + '...' : trimmed;
      }
    }
  }
  
  return null;
}

/**
 * Función principal de parsing de documentos
 * Extrae datos del texto SIN usar LLM
 */
export function parseDocument(text: string): ExtractedDocumentData {
  const expediente = extractExpedienteNumber(text);
  const dates = extractDates(text);
  const plazo = extractPlazo(text);
  const interesado = extractInterestedParty(text);
  const categoryResult = detectCategory(text);
  const title = generateTitle(text, categoryResult.category);
  
  // Determinar fecha del documento (la más antigua) y fecha límite (la más reciente)
  let documentDate: Date | null = null;
  let dueDate: Date | null = null;
  
  if (dates.dates.length >= 2) {
    // Ordenar por fecha
    const sortedDates = [...dates.dates].sort((a, b) => a.getTime() - b.getTime());
    documentDate = sortedDates[0];
    dueDate = sortedDates[sortedDates.length - 1];
  } else if (dates.dates.length === 1) {
    documentDate = dates.dates[0];
    // Si hay plazo, calcular fecha límite aproximada
    if (plazo.days) {
      dueDate = new Date(documentDate);
      dueDate.setDate(dueDate.getDate() + plazo.days);
    }
  }
  
  return {
    expedienteNumber: expediente.value,
    title,
    interestedParty: interesado.value,
    documentDate,
    dueDate,
    deadlineDays: plazo.days,
    deadlineType: plazo.type,
    category: categoryResult.category,
    confidence: {
      expediente: expediente.confidence,
      dates: dates.confidence,
      category: categoryResult.confidence,
      interested: interesado.confidence,
    },
  };
}

/**
 * Calcula el nivel de confianza general de la extracción
 */
export function getOverallConfidence(data: ExtractedDocumentData): number {
  const weights = {
    expediente: 0.2,
    dates: 0.3,
    category: 0.25,
    interested: 0.25,
  };
  
  return (
    data.confidence.expediente * weights.expediente +
    data.confidence.dates * weights.dates +
    data.confidence.category * weights.category +
    data.confidence.interested * weights.interested
  );
}
